import React, { useEffect } from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';
import {
    IconLinkedIn,
    IconX
} from '$partials/icons';
import { usePage } from '$contexts/page.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Footer}
 */
const Footer = ({
    copyrightNavigation = '',
    copyrightText = '',
    ctaLabel = '',
    ctaLink = null,
    exploreColour = '',
    exploreHeading = '',
    exploreHide = false,
    exploreTags = null,
    exploreTitle = '',
    linkedinUrl = '',
    logo = null,
    magazineCtaCssClass = '',
    magazineCtaMobileUrlOverride = '',
    magazineColour = '',
    magazineCtaLabel = '',
    magazineCtaLink = null,
    magazineHeading = '',
    magazineHide = false,
    magazineImage = null,
    magazineTitle = '',
    navigationColumn1 = '',
    navigationColumn2 = '',
    navigationColumn3 = '',
    newsletterCtaCssClass = '',
    newsletterCtaMobileUrlOverride = '',
    newsletterColour = '',
    newsletterCtaLabel = '',
    newsletterCtaLink = null,
    newsletterHeading = '',
    newsletterHide = false,
    newsletterTitle = '',
    pageType = '',
    xUrl = ''
}) => {
    const { page } = usePage();

    // misc
    const splitColClass = (exploreHide || magazineHide) ? ' w-full' : ' w-full sm:w-6/12';

    // Apply PostHog data attributes to rich text navigation links
    useEffect(() => {
        const navigation = document.getElementById('secondary-navigation');

        if (navigation) {
            const links = navigation.querySelectorAll('a');

            for (const [i, link] of links.entries()) {
                link.dataset.analyticsAction = 'click';
                link.dataset.analyticsContext = 'navigation';
                link.dataset.analyticsDestination = link.href;
                link.dataset.analyticsId = `${page?.id}footer_link_${i}`;
                link.dataset.analyticsLabel = link.innerText;
                link.dataset.analyticsLocation = 'footer';
                link.dataset.analyticsModule = 'footer_navigation_link';
            }
        }
    }, []);

    return (
        <footer>
            <div className='layout-container layout-site-outer-padding'>
                <div className='col-wrapper-breakout flex flex-wrap gap-y-8 md:gap-y-16'>
                    {/* Explore */}
                    {!exploreHide && (
                        <div className={`sm:pr-4 md:pr-8${splitColClass}`}>
                            <div className={`h-full flex flex-col gap-24 md:gap-40 -mx-4 sm:mr-0 md:-ml-8 px-8 md:px-16 pb-8 md:pb-16 pt-24 rounded-lg overflow-hidden bg-surface-panel-${exploreColour}`}>
                                <div className='flex flex-col gap-8'>
                                    {exploreTitle && (
                                        <h2 className='type-style-sans-h1'>
                                            {exploreTitle}
                                        </h2>
                                    )}

                                    {exploreHeading && (
                                        <p className='w-5/6 type-style-sans-h3'>
                                            {exploreHeading}
                                        </p>
                                    )}
                                </div>

                                {exploreTags?.edges?.length > 0 && (
                                    <div>
                                        <ul className='flex flex-wrap gap-10'>
                                            {exploreTags.edges.map((itm, idx) => {
                                                if (!itm.node) {
                                                    return null;
                                                }

                                                return (
                                                    <li key={idx}>
                                                        <a
                                                            className='button-outcome'
                                                            data-analytics-action='click'
                                                            data-analytics-context='explore'
                                                            data-analytics-destination={itm.node.url}
                                                            data-analytics-id={`${page?.id}_footer_explore_topic_${itm.node.id}`}
                                                            data-analytics-label={itm.node.title}
                                                            data-analytics-location='footer'
                                                            data-analytics-module='footer_explore'
                                                            href={itm.node.url}>
                                                            {itm.node.title}
                                                        </a>
                                                    </li>
                                                );
                                            }).filter(Boolean)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Magazine */}
                    {!magazineHide && (
                        <div className={`sm:pl-4 md:pl-8${splitColClass}`}>
                            <div
                                className={`h-full flex flex-col gap-24 md:gap-40 -mx-4 sm:ml-0 md:-mr-8 px-8 md:px-16 pb-8 md:pb-16 pt-24 rounded-lg overflow-hidden bg-no-repeat bg-cover bg-right-bottom bg-surface-panel-${magazineColour}`}
                                style={{ backgroundImage: `url(${magazineImage?.px2})` }}>
                                <div className='flex flex-col gap-8'>
                                    {magazineTitle && (
                                        <h2 className='type-style-sans-h1'>
                                            {magazineTitle}
                                        </h2>
                                    )}

                                    {magazineHeading && (
                                        <p className='w-5/6 type-style-sans-h3'>
                                            {magazineHeading}
                                        </p>
                                    )}
                                </div>

                                {magazineCtaLabel && (
                                    <div>
                                        {magazineCtaMobileUrlOverride ? (
                                            <>
                                                <a
                                                    className='sm:hidden button-primary'
                                                    data-analytics-action='click'
                                                    data-analytics-context='magazine_subscribe'
                                                    data-analytics-destination={magazineCtaMobileUrlOverride}
                                                    data-analytics-id={`${page?.id}_footer_magazine_cta`}
                                                    data-analytics-label={magazineCtaLabel}
                                                    data-analytics-location='footer'
                                                    data-analytics-module='footer_magazine_cta'
                                                    href={magazineCtaMobileUrlOverride}
                                                    rel='noreferrer'
                                                    target='_blank'>
                                                    {magazineCtaLabel}
                                                </a>
                                                <a
                                                    className={`hidden sm:inline-block button-primary ${magazineCtaCssClass ? ' ' + magazineCtaCssClass: ''}`}
                                                    data-analytics-action='click'
                                                    data-analytics-context='magazine_subscribe'
                                                    data-analytics-destination={formatLinkBlockToUrl(magazineCtaLink)}
                                                    data-analytics-id={`${page?.id}_footer_magazine_cta`}
                                                    data-analytics-label={magazineCtaLabel}
                                                    data-analytics-location='footer'
                                                    data-analytics-module='footer_magazine_cta'
                                                    href={formatLinkBlockToUrl(magazineCtaLink)}
                                                    rel='noreferrer'
                                                    target='_blank'>
                                                    {magazineCtaLabel}
                                                </a>
                                            </>
                                        ) : (
                                            <a
                                                className={`button-primary ${magazineCtaCssClass ? ' ' + magazineCtaCssClass: ''}`}
                                                data-analytics-action='click'
                                                data-analytics-context='magazine_subscribe'
                                                data-analytics-destination={formatLinkBlockToUrl(magazineCtaLink)}
                                                data-analytics-id={`${page?.id}_footer_magazine_cta`}
                                                data-analytics-label={magazineCtaLabel}
                                                data-analytics-location='footer'
                                                data-analytics-module='footer_magazine_cta'
                                                href={formatLinkBlockToUrl(magazineCtaLink)}
                                                rel='noreferrer'
                                                target='_blank'>
                                                {magazineCtaLabel}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Newsletter */}
                {(!newsletterHide && (newsletterTitle || newsletterHeading || newsletterCtaLabel)) && (
                    <div className={`col-wrapper-breakout bg-surface-panel-${newsletterColour} rounded-lg mt-8 md:mt-16 pb-8 md:pb-16 pt-24`}>
                        <div className='flex flex-col gap-24 md:gap-40 col-padding'>
                            <div className='flex flex-col gap-8'>
                                {newsletterTitle && (
                                    <h2 className='w-full sm:w-6/12 type-style-sans-h1'>
                                        {newsletterTitle}
                                    </h2>
                                )}

                                {newsletterHeading && (
                                    <p className='w-full sm:w-5/6 type-style-sans-h3'>
                                        {newsletterHeading}
                                    </p>
                                )}
                            </div>

                            <div>
                                {newsletterCtaMobileUrlOverride ? (
                                    <>
                                        <a
                                            className='sm:hidden button-primary'
                                            data-analytics-action='click'
                                            data-analytics-context='newsletter_subscribe'
                                            data-analytics-destination={newsletterCtaMobileUrlOverride}
                                            data-analytics-id={`${page?.id}_footer_newsletter_cta`}
                                            data-analytics-label={newsletterCtaLabel}
                                            data-analytics-location='footer'
                                            data-analytics-module='footer_newsletter_cta'
                                            href={newsletterCtaMobileUrlOverride}
                                            rel='noreferrer'
                                            target='_blank'>
                                            {newsletterCtaLabel}
                                        </a>
                                        <a
                                            className={`hidden sm:inline-block button-primary ${newsletterCtaCssClass ? ' ' + newsletterCtaCssClass: ''}`}
                                            data-analytics-action='click'
                                            data-analytics-context='newsletter_subscribe'
                                            data-analytics-destination={formatLinkBlockToUrl(newsletterCtaLink)}
                                            data-analytics-id={`${page?.id}_footer_newsletter_cta`}
                                            data-analytics-label={newsletterCtaLabel}
                                            data-analytics-location='footer'
                                            data-analytics-module='footer_newsletter_cta'
                                            href={formatLinkBlockToUrl(newsletterCtaLink)}
                                            rel='noreferrer'
                                            target='_blank'>
                                            {newsletterCtaLabel}
                                        </a>
                                    </>
                                ) : (
                                    <a
                                        className={`button-primary ${newsletterCtaCssClass ? ' ' + newsletterCtaCssClass: ''}`}
                                        data-analytics-action='click'
                                        data-analytics-context='newsletter_subscribe'
                                        data-analytics-destination={formatLinkBlockToUrl(newsletterCtaLink)}
                                        data-analytics-id={`${page?.id}_footer_newsletter_cta`}
                                        data-analytics-label={newsletterCtaLabel}
                                        data-analytics-location='footer'
                                        data-analytics-module='footer_newsletter_cta'
                                        href={formatLinkBlockToUrl(newsletterCtaLink)}
                                        rel='noreferrer'
                                        target='_blank'>
                                        {newsletterCtaLabel}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className='col-wrapper flex flex-wrap justify-between pb-16 pt-48'>
                    <div className='w-11/12 sm:w-3/12 col-padding flex flex-col gap-24'>
                        {/* Logo */}
                        {(logo && pageType === 'Home') && (
                            <img
                                alt='Business Leader Logo'
                                className='w-full pointer-events-none select-none'
                                height={logo.height}
                                loading='eager'
                                src={logo.px2}
                                width={logo.width} />
                        )}

                        {(logo && pageType !== 'Home') && (
                            <a
                                aria-label='Back to homepage'
                                href='/'>
                                <img
                                    alt='Business Leader Logo'
                                    className='w-full pointer-events-none select-none'
                                    height={logo.height}
                                    loading='eager'
                                    src={logo.px2}
                                    width={logo.width} />
                            </a>
                        )}

                        {/* Social */}
                        <ul className='flex gap-8'>
                            {/* CTA */}
                            {(ctaLink && ctaLabel) && (
                                <li>
                                    <a
                                        className='button-outcome-xl w-full justify-start'
                                        data-analytics-action='click'
                                        data-analytics-context='contact'
                                        data-analytics-destination={formatLinkBlockToUrl(ctaLink)}
                                        data-analytics-id={`${page?.id}_footer_contact`}
                                        data-analytics-label={ctaLabel}
                                        data-analytics-location='footer'
                                        data-analytics-module='footer_contact_cta'
                                        href={formatLinkBlockToUrl(ctaLink)}>
                                        {ctaLabel}
                                    </a>
                                </li>
                            )}

                            {linkedinUrl && (
                                <li>
                                    <a
                                        aria-label='Linked In'
                                        className='button-icon-outcome-xl'
                                        href={linkedinUrl}
                                        rel='noreferrer'
                                        target='_blank'>
                                        <IconLinkedIn />
                                    </a>
                                </li>
                            )}

                            {xUrl && (
                                <li>
                                    <a
                                        aria-label='X'
                                        className='button-icon-outcome-xl'
                                        href={xUrl}
                                        rel='noreferrer'
                                        target='_blank'>
                                        <IconX />
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Navigation */}
                    <nav
                        className='w-full sm:w-6/12 pt-48 sm:pt-0 flex flex-wrap gap-y-16 rich-text-footer'
                        id='secondary-navigation'>
                        {navigationColumn1 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn1 }} />
                        )}

                        {navigationColumn2 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn2 }} />
                        )}

                        {navigationColumn3 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn3 }} />
                        )}
                    </nav>
                </div>

                {/* Copyright Navigation */}
                <div className='pb-16 pt-40'>
                    <nav
                        className='type-style-sans-body text-text-default-secondary flex flex-col sm:flex-row gap-8 sm:gap-16 rich-text-copyright'
                        dangerouslySetInnerHTML={{ __html: copyrightNavigation }}
                        id='legal-navigation' />
                </div>

                {/* Copyright Text */}
                <div className='col-wrapper pb-16'>
                    <div className='col-padding'>
                        <div
                            className='type-style-sans-body text-text-default-secondary'
                            dangerouslySetInnerHTML={{ __html: copyrightText }} />
                    </div>
                </div>
            </div>
        </footer>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default Footer;
